import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { SimpleMenuComponent } from './simple-menu.component';

/**
 * This service is for SimplePanel components.
 */
@Injectable()
export class SimplePanelService {
  /** The element for this first SimpleMenu component that is a designated a main menu */
  private _mainMenu: SimpleMenuComponent | undefined;
  /** Fired when state of main menu changes */
  private mainMenuChangeSubject = new BehaviorSubject<'none' | 'minimized' | 'expanded'>('none');
  /** Subscription on mainMenuExpansionChange */
  private mainMenuExpansionChangeSubscription: Subscription | undefined;

  /** The class name of the root panel for all of the SimpleX controls */
  rootPanelClassName = 'contentPanel';

  readonly mainMenuChange$ = this.mainMenuChangeSubject.pipe(distinctUntilChanged());

  /** called when the window resizes */
  readonly onResize = new Subject<number>();

  constructor() { }

  /** gets and sets the mainMenu Element */
  get mainMenu() {
    return this._mainMenu;
  }
  set mainMenu(val: SimpleMenuComponent | undefined) {
    if (this._mainMenu !== val) {
      if (this.mainMenuExpansionChangeSubscription) {
        this.mainMenuExpansionChangeSubscription.unsubscribe();
      }
      const oldMainMenu = this._mainMenu;
      this._mainMenu = val;

      if (oldMainMenu?.isMainMenu) {
        // keep isMainMenu in sync on old menu
        oldMainMenu.isMainMenu = false;
      }
      if (this._mainMenu) {
        this.mainMenuExpansionChangeSubscription = this._mainMenu.isExpandedChange.pipe(
          tap(isExpanded => this.mainMenuChangeSubject.next(isExpanded ? 'expanded' : 'minimized')))
          .subscribe();
      } else {
        this.mainMenuChangeSubject.next('none');
      }
    }
  }

  /** returns true if there is a Main Menu and it is expanded */
  get mainMenuIsExpanded() {
    return this._mainMenu?.isExpanded;
  }

  /**
   * Make sure that hosts have proper display all the way until flex parent for a SimpleX Control
   */
  ensureCompatibleHostCss(rootTemplateElementRef: ElementRef | undefined) {
    // this is some pretty specific code that will change all parent elements into display flex if they
    // are between this and another flex element or an elemenet with .contentPanel
    // the issue here is that inline element get changed into block by display flex.
    let curElement = rootTemplateElementRef?.nativeElement?.parentElement;
    const candidatesToUpdate: HTMLElement[] = [];
    let foundFlexParent = false;
    const rootPanelClassTest = new RegExp(`\\b${this.rootPanelClassName}\\b`);

    while (curElement) {
      const style = window.getComputedStyle(curElement);

      if (style.display === 'flex' || style.position === 'absolute' || style.position === 'fixed' ||
        rootPanelClassTest.test(curElement.className)) {
        foundFlexParent = true;
        break;
      }

      if (style.display !== 'none') {
        candidatesToUpdate.push(curElement)
      }

      curElement = curElement.parentElement;
    }

    if (foundFlexParent) {
      for (const elem of candidatesToUpdate) {
        elem.style.display = 'inherit';
        elem.style.height = '100%';
        elem.style.flex = '0 0 auto';
      }
    }
  }
}
